import { useTranslation } from 'react-i18next';
import { useCurrentStaff } from '@/auth/useCurrentStaff';
import { ErrorState, LoadingState } from '@/components/ui/states';

/**
 * The console's landing screen.
 *
 * Its job in M1-32 is to make the capability context *visible*: it prints the
 * persona and the exact permission set the API returned. That is deliberately
 * more than decoration — when a later slice 403s unexpectedly, the first
 * question is "what does the API think this person can do?", and this screen
 * answers it without anyone opening dev tools.
 */
export function Overview() {
  const { t } = useTranslation();
  const { data, isPending, isError, refetch } = useCurrentStaff();

  if (isPending) {
    return <LoadingState />;
  }

  if (isError || !data) {
    return <ErrorState onRetry={() => void refetch()} />;
  }

  return (
    <>
      <h1 className="text-xl font-semibold">{t('overview.title')}</h1>
      <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
        {t('overview.signedInAs', { persona: data.persona })}
      </p>

      <h2 className="mt-8 text-sm font-medium">{t('overview.capabilitiesTitle')}</h2>
      {data.permissions.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1">
          {data.permissions.map((permission) => (
            <li key={permission} className="font-mono text-sm">
              {permission}
            </li>
          ))}
        </ul>
      ) : (
        // Says *why* it is empty rather than rendering nothing. An empty list
        // here is a real state (an enrolment with no persona match), and one
        // that looks identical to a broken screen if it says nothing.
        <p className="mt-2 max-w-prose text-sm text-[var(--color-muted-foreground)]">
          {t('overview.noCapabilities')}
        </p>
      )}
    </>
  );
}
